<script setup lang="ts">
  import { computed, ref, watch, onMounted } from 'vue'
  import { useRouter, useRoute, type RouteLocationRaw } from 'vue-router'
  import MeetingRoom from '@shared/components/meeting/meeting-room.vue'

  import { ERouterList } from '@internal/router/constants/router-list'
  import { EMeetingRole } from '@shared/types/constants/meeting-role'
  import { ESummaryTabList } from '@internal/types/constants/summary-tab-list'

  import { useComposition } from '@shared/utils/composables/meeting'
  import { initPinPMode } from '@shared/utils/helpers/meeting'
  import { appHeight } from '@shared/utils/helpers'
  import { useEventListener } from '@shared/utils/composables/event-listener'

  const route = useRoute()
  const router = useRouter()
  useEventListener(window, 'resize', appHeight)

  const parent = ref<HTMLElement>()
  const child = ref<HTMLElement>()

  const { isShownMeetingRoom, isPinPMode, patientName, videoConference } = useComposition()

  const isPinPModeStyles = computed(() => {
    return isPinPMode.value ? 'tw-absolute' : 'tw-fixed tw-h-[100dvh] tw-w-full tw-h-full tw-top-0'
  })

  const removeListeners = ref<() => void>()

  watch(isPinPMode, () => {
    removeListeners.value && removeListeners.value()
    if (isPinPMode.value && parent.value && child.value) {
      removeListeners.value = initPinPMode({
        parentComponent: parent.value,
        childComponent: child.value,
        paddingX: 8,
        paddingY: 88,
      })
    }
  })

  onMounted(() => {
    appHeight()
  })

  const visitPage = computed(() => {
    if (!videoConference.value) {
      return null
    }

    return {
      name: ERouterList.VISIT,
      params: {
        ...route.params,
        visitId: videoConference.value.visitId,
      },
    }
  })

  const meetingPreviewPage = computed(() => {
    return {
      name: ERouterList.VISIT_MEETING_PREVIEW,
      params: {
        ...route.params,
      },
    }
  })

  const visitMeetingPage = computed(() => {
    if (!videoConference.value) {
      return null
    }
    return {
      name: ERouterList.VISIT_MEETING,
      params: { ...route.params, visitId: videoConference.value.visitId },
    }
  })

  const visitPagePartTab = computed(() => {
    if (!videoConference.value) {
      return null
    }
    return {
      name: ERouterList.VISIT,
      params: { ...route.params, visitId: videoConference.value.visitId },
      query: {
        summaryTab: ESummaryTabList.PARTICIPANTS,
        openInvitePopup: 'true',
      },
    }
  })

  const redirectTo = (routeSettings: RouteLocationRaw | null) => {
    if (routeSettings) {
      router.push(routeSettings)
    }
  }
</script>

<template>
  <div ref="parent" class="tw-h-[100dvh]">
    <router-view />
    <div v-if="isShownMeetingRoom" ref="child" :class="isPinPModeStyles" class="tw-flex tw-flex-col tw-z-20">
      <meeting-room
        :local-role="EMeetingRole.HOST"
        :patient-name="patientName"
        :video-conference="videoConference"
        @on-leave="redirectTo(visitPage)"
        @on-redirect-preview="redirectTo(meetingPreviewPage)"
        @on-exit-pip="redirectTo(visitMeetingPage)"
        @on-pip="redirectTo(visitPage)"
        @on-invite-contact="redirectTo(visitPagePartTab)"
      />
    </div>
  </div>
</template>
